import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Moveable from "react-moveable";
import Selecto from "react-selecto";
import { Box, Divider, IconButton, Tooltip, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";
import UndoRounded from "@mui/icons-material/UndoRounded";
import RedoRounded from "@mui/icons-material/RedoRounded";
import ZoomInRounded from "@mui/icons-material/ZoomInRounded";
import ZoomOutRounded from "@mui/icons-material/ZoomOutRounded";
import FitScreenRounded from "@mui/icons-material/FitScreenRounded";
import GridOnRounded from "@mui/icons-material/GridOnRounded";
import GridOffRounded from "@mui/icons-material/GridOffRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import DeleteRounded from "@mui/icons-material/DeleteRounded";
import FlipToFrontRounded from "@mui/icons-material/FlipToFrontRounded";
import FlipToBackRounded from "@mui/icons-material/FlipToBackRounded";
import AlignHorizontalLeftRounded from "@mui/icons-material/AlignHorizontalLeftRounded";
import AlignHorizontalCenterRounded from "@mui/icons-material/AlignHorizontalCenterRounded";
import AlignHorizontalRightRounded from "@mui/icons-material/AlignHorizontalRightRounded";
import AlignVerticalTopRounded from "@mui/icons-material/AlignVerticalTopRounded";
import AlignVerticalCenterRounded from "@mui/icons-material/AlignVerticalCenterRounded";
import AlignVerticalBottomRounded from "@mui/icons-material/AlignVerticalBottomRounded";
import CanvasSheet from "./CanvasSheet";
import { ELEMENT_TYPES, PX_PER_MM, clampToPage, minSizeFor, newElementId, round1 } from "./canvasModel";

const BLUE = "#2563EB";
const BORDER = "#E2E8F0";
const INK_MUTED = "#64748B";

const toMm = (px) => round1(px / PX_PER_MM);

// Studio-only look: hover outline, overflow warning, ghosted hidden elements.
// Scoped to .tc-editing so none of it can reach a printed page.
const EDITOR_CSS = `
.tc-editing .tc-el:hover { outline: 1px dashed rgba(37,99,235,0.55); outline-offset: 0; }
.tc-editing .tc-el.tc-selected { outline: none; }
.tc-editing .tc-el.tc-hidden { outline: 1px dotted #94A3B8; }
.tc-editor-stage .moveable-control-box { z-index: 20 !important; }
.tc-editor-stage .moveable-control-box .moveable-control { border-color: #fff; background: ${BLUE}; }
.tc-editor-stage .moveable-control-box .moveable-line { background: ${BLUE}; }
`;

/**
 * PowerPoint-style editor for one face of a free-form ticket.
 *
 * Elements live in the parent (history-backed). This component only turns
 * pointer/keyboard input into element updates: select (click, shift-click,
 * marquee), move, resize (8 handles; Shift or the QR keeps proportions), rotate,
 * snap to the other elements / page centre / safe margin / grid, nudge with the
 * arrows, layer, align, duplicate, copy/paste, delete, undo/redo.
 *
 * During a gesture moveable writes px straight onto the DOM node; the result is
 * committed back to state in mm when the gesture ends.
 */
const TicketCanvasEditor = ({
  history,
  side,
  onSideChange,
  showBackTab,
  selectedIds,
  onSelect,
  paperW,
  paperH,
  safeMarginMm,
  sheetProps,
  onAddElement,
  onRequestEdit,
  // The OTHER copy of a two-copies-per-sheet ticket, drawn read-only beside
  // the editable one with the cut line between them, as it will print.
  // { position: "left" | "right", label, render(scale), onActivate }
  companion,
}) => {
  const { elements, set, undo, redo, canUndo, canRedo } = history;
  const stageRef = useRef(null);
  const sheetRef = useRef(null);
  const moveableRef = useRef(null);
  const selectoRef = useRef(null);
  const clipboard = useRef([]);
  const [zoom, setZoom] = useState(null); // null = fit to width
  const [fitScale, setFitScale] = useState(1);
  // Off by default: a plain white sheet reads as "the ticket" to clients; the
  // grid is opt-in from the toolbar for anyone lining things up by eye.
  const [showGrid, setShowGrid] = useState(false);
  const [snapGrid, setSnapGrid] = useState(false);
  const [shiftDown, setShiftDown] = useState(false);
  const [targets, setTargets] = useState([]);
  const GRID_MM = 5;

  const scale = zoom ?? fitScale;
  const sideCount = {
    front: elements.filter((el) => el.side !== "back").length,
    back: elements.filter((el) => el.side === "back").length,
  };
  const sideEls = useMemo(() => elements.filter((el) => el.side === side), [elements, side]);
  const byId = useMemo(() => Object.fromEntries(elements.map((el) => [el.id, el])), [elements]);
  const selected = selectedIds.map((id) => byId[id]).filter(Boolean);

  // ─── Fit-to-width zoom ────────────────────────────────────────────────────
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const compute = () => {
      const avail = stage.clientWidth - 48;
      // With a companion the whole landscape sheet (both halves) must fit.
      const widthMm = paperW * (companion ? 2 : 1);
      const next = Math.min(1.5, Math.max(0.25, avail / (widthMm * PX_PER_MM)));
      setFitScale(Number.isFinite(next) ? next : 1);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(stage);
    return () => ro.disconnect();
  }, [paperW, !!companion]);

  // ─── Selected ids → DOM targets for moveable ──────────────────────────────
  useLayoutEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    const next = selectedIds
      .filter((id) => byId[id] && byId[id].side === side && !byId[id].locked)
      .map((id) => sheet.querySelector(`[data-el-id="${id}"]`))
      .filter(Boolean);
    setTargets((prev) =>
      prev.length === next.length && prev.every((t, i) => t === next[i]) ? prev : next,
    );
  }, [selectedIds, byId, side, elements]);

  // Keep moveable's box glued to its targets after any re-render that moved
  // them (undo, numeric edit in the properties panel, zoom).
  useEffect(() => {
    moveableRef.current?.updateRect();
  });

  // ─── Element updates ──────────────────────────────────────────────────────
  const patch = useCallback(
    (changes, mergeKey) =>
      set(
        (list) => list.map((el) => (changes[el.id] ? { ...el, ...changes[el.id] } : el)),
        mergeKey,
      ),
    [set],
  );

  const removeSelected = useCallback(() => {
    if (!selectedIds.length) return;
    const ids = new Set(selectedIds.filter((id) => !byId[id]?.locked));
    set((list) => list.filter((el) => !ids.has(el.id)));
    onSelect([]);
  }, [selectedIds, byId, set, onSelect]);

  const cloneEls = useCallback(
    (els, offset) => {
      const maxZ = Math.max(0, ...elements.map((e) => e.z || 0));
      return els.map((el, i) => ({
        ...JSON.parse(JSON.stringify(el)),
        id: newElementId(),
        side,
        x: round1(el.x + offset),
        y: round1(el.y + offset),
        z: maxZ + i + 1,
        locked: false,
      }));
    },
    [elements, side],
  );

  const duplicate = useCallback(() => {
    if (!selected.length) return;
    const copies = cloneEls(selected, 4);
    set((list) => [...list, ...copies]);
    onSelect(copies.map((c) => c.id));
  }, [selected, cloneEls, set, onSelect]);

  const paste = useCallback(() => {
    if (!clipboard.current.length) return;
    const copies = cloneEls(clipboard.current, 4);
    clipboard.current = copies; // the next paste cascades further
    set((list) => [...list, ...copies]);
    onSelect(copies.map((c) => c.id));
  }, [cloneEls, set, onSelect]);

  const layer = useCallback(
    (dir) => {
      if (!selected.length) return;
      const zs = elements.map((e) => e.z || 0);
      const top = Math.max(0, ...zs);
      const bottom = Math.min(0, ...zs);
      const changes = {};
      selected.forEach((el, i) => {
        changes[el.id] = { z: dir > 0 ? top + 1 + i : bottom - selected.length + i };
      });
      patch(changes);
    },
    [selected, elements, patch],
  );

  // Align to each other when several are selected, to the page when one is.
  const align = useCallback(
    (how) => {
      const els = selected.filter((el) => !el.locked);
      if (!els.length) return;
      const box =
        els.length > 1
          ? {
              l: Math.min(...els.map((e) => e.x)),
              t: Math.min(...els.map((e) => e.y)),
              r: Math.max(...els.map((e) => e.x + e.w)),
              b: Math.max(...els.map((e) => e.y + e.h)),
            }
          : { l: 0, t: 0, r: paperW, b: paperH };
      const changes = {};
      els.forEach((el) => {
        const c = {};
        if (how === "left") c.x = box.l;
        if (how === "hcenter") c.x = (box.l + box.r) / 2 - el.w / 2;
        if (how === "right") c.x = box.r - el.w;
        if (how === "top") c.y = box.t;
        if (how === "vcenter") c.y = (box.t + box.b) / 2 - el.h / 2;
        if (how === "bottom") c.y = box.b - el.h;
        Object.keys(c).forEach((k) => (c[k] = round1(c[k])));
        changes[el.id] = c;
      });
      patch(changes);
    },
    [selected, paperW, paperH, patch],
  );

  // ─── Keyboard ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const isTyping = (t) =>
      t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
    const onKeyDown = (e) => {
      if (e.key === "Shift") setShiftDown(true);
      if (isTyping(e.target)) return;
      // A dialog (save template etc.) is open — leave its keys alone.
      if (document.querySelector(".MuiDialog-root")) return;
      const mod = e.ctrlKey || e.metaKey;
      const k = e.key.toLowerCase();
      if (mod && k === "z" && !e.shiftKey) { e.preventDefault(); undo(); return; }
      if (mod && (k === "y" || (k === "z" && e.shiftKey))) { e.preventDefault(); redo(); return; }
      if (mod && k === "a") { e.preventDefault(); onSelect(sideEls.map((el) => el.id)); return; }
      if (!selectedIds.length) {
        if (mod && k === "v") { e.preventDefault(); paste(); }
        return;
      }
      if (k === "delete" || k === "backspace") { e.preventDefault(); removeSelected(); return; }
      if (k === "escape") { onSelect([]); return; }
      if (mod && k === "d") { e.preventDefault(); duplicate(); return; }
      if (mod && k === "c") { clipboard.current = JSON.parse(JSON.stringify(selected)); return; }
      if (mod && k === "v") { e.preventDefault(); paste(); return; }
      if (mod && e.key === "]") { e.preventDefault(); layer(1); return; }
      if (mod && e.key === "[") { e.preventDefault(); layer(-1); return; }
      const step = e.shiftKey ? 5 : 0.5;
      const d = { arrowleft: [-step, 0], arrowright: [step, 0], arrowup: [0, -step], arrowdown: [0, step] }[k];
      if (d) {
        e.preventDefault();
        const changes = {};
        selected.filter((el) => !el.locked).forEach((el) => {
          // Nudging stops at the paper's edge.
          const c = clampToPage({ ...el, x: el.x + d[0], y: el.y + d[1] }, paperW, paperH);
          changes[el.id] = { x: c.x, y: c.y };
        });
        patch(changes, "nudge");
      }
    };
    const onKeyUp = (e) => {
      if (e.key === "Shift") setShiftDown(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [undo, redo, onSelect, sideEls, selectedIds, selected, removeSelected, duplicate, paste, layer, patch]);

  // Ctrl + wheel zooms the stage (like any design tool) instead of the page.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const onWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      setZoom((z) => {
        const cur = z ?? fitScale;
        return Math.min(3, Math.max(0.25, cur * (e.deltaY < 0 ? 1.1 : 1 / 1.1)));
      });
    };
    stage.addEventListener("wheel", onWheel, { passive: false });
    return () => stage.removeEventListener("wheel", onWheel);
  }, [fitScale]);

  // ─── Drop from the element palette ────────────────────────────────────────
  const onDrop = (e) => {
    const raw = e.dataTransfer.getData("application/x-ticket-element");
    if (!raw || !sheetRef.current) return;
    e.preventDefault();
    const { type, opts } = JSON.parse(raw);
    const r = sheetRef.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * paperW;
    const y = ((e.clientY - r.top) / r.height) * paperH;
    onAddElement(type, opts, { x, y, centered: true });
  };

  // ─── Snap guides (sheet-local px) ─────────────────────────────────────────
  const vGuides = [0, safeMarginMm, paperW / 2, paperW - safeMarginMm, paperW].map((mm) => mm * PX_PER_MM);
  const hGuides = [0, safeMarginMm, paperH / 2, paperH - safeMarginMm, paperH].map((mm) => mm * PX_PER_MM);
  const otherEls = useMemo(() => {
    const sheet = sheetRef.current;
    if (!sheet) return [];
    const sel = new Set(selectedIds);
    return Array.from(sheet.querySelectorAll(".tc-el")).filter((n) => !sel.has(n.dataset.elId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets, elements, side]);

  const single = selected.length === 1 ? selected[0] : null;
  const keepRatio = shiftDown || (single && ELEMENT_TYPES[single.type]?.keepRatio);

  // Commit helpers — read what moveable left on the node, then keep the box
  // at least its minimum size and fully on the paper (a box dragged half off
  // the sheet would print half missing).
  const commitFrame = (target, extra = {}) => {
    const id = target.dataset.elId;
    const s = target.style;
    const frame = clampToPage(
      {
        ...byId[id],
        x: toMm(parseFloat(s.left)),
        y: toMm(parseFloat(s.top)),
        w: toMm(parseFloat(s.width)),
        h: toMm(parseFloat(s.height)),
      },
      paperW,
      paperH,
    );
    // Write the clamped frame back onto the node. When it equals the stored
    // value React sees no change and would leave the dragged px on screen.
    s.left = `${frame.x}mm`;
    s.top = `${frame.y}mm`;
    s.width = `${frame.w}mm`;
    s.height = `${frame.h}mm`;
    return { [id]: { x: frame.x, y: frame.y, w: frame.w, h: frame.h, ...extra } };
  };
  // Live floor while resizing (px, the units moveable works in). For several
  // boxes at once the smallest floor among them.
  const minPx = (() => {
    const els = targets.map((t) => byId[t.dataset.elId]).filter(Boolean);
    if (!els.length) return [0, 0];
    const mins = els.map(minSizeFor);
    return [
      Math.min(...mins.map((m) => m[0])) * PX_PER_MM,
      Math.min(...mins.map((m) => m[1])) * PX_PER_MM,
    ];
  })();
  // moveable reports px; the node is styled in mm until the first gesture, so
  // pin it to px at gesture start (identical position) to keep the maths in one unit.
  const pinPx = (target) => {
    const id = target.dataset.elId;
    const el = byId[id];
    if (!el) return;
    target.style.left = `${el.x * PX_PER_MM}px`;
    target.style.top = `${el.y * PX_PER_MM}px`;
    target.style.width = `${el.w * PX_PER_MM}px`;
    target.style.height = `${el.h * PX_PER_MM}px`;
  };

  const toolBtn = (title, icon, onClick, disabled) => (
    <Tooltip title={title} arrow>
      <span>
        <IconButton size="small" onClick={onClick} disabled={disabled} sx={{ borderRadius: 1.5 }}>
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  const singleEl = single;
  const ov = singleEl
    ? `${singleEl.x} , ${singleEl.y} mm · ${singleEl.w} × ${singleEl.h} mm${singleEl.rotation ? ` · ${singleEl.rotation}°` : ""}`
    : selected.length > 1
    ? `${selected.length} selected`
    : "Click an element to select · drag to move · handles to resize";

  // Caption over each half of a two-copy sheet; the active one is blue.
  const copyCaption = (text, active) => (
    <Typography
      sx={{
        position: "absolute",
        top: -20,
        left: 0,
        fontSize: "0.62rem",
        fontWeight: 700,
        letterSpacing: "0.6px",
        textTransform: "uppercase",
        color: active ? BLUE : INK_MUTED,
        whiteSpace: "nowrap",
      }}
    >
      {active ? `Editing · ${text}` : text}
    </Typography>
  );

  // The other copy: read-only, dimmed, and one click away from being the one
  // you edit. The dashed rule between the halves is the cut line.
  const renderCompanion = () => (
    <Box
      sx={{
        position: "relative",
        order: companion.position === "left" ? 0 : 4,
        display: "flex",
        flexDirection: companion.position === "left" ? "row" : "row-reverse",
      }}
    >
      <Box
        onClick={companion.onActivate}
        title={companion.activatable ? `Click to edit the ${companion.label}` : companion.label}
        sx={{
          position: "relative",
          cursor: companion.activatable ? "pointer" : "default",
          opacity: 0.72,
          transition: "opacity .15s",
          "&:hover": { opacity: companion.activatable ? 1 : 0.72 },
          "& *": { pointerEvents: "none" },
        }}
      >
        {copyCaption(companion.label, false)}
        {companion.render(scale)}
      </Box>
      <Box sx={{ width: 0, borderLeft: "1px dashed #94A3B8", alignSelf: "stretch" }} />
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, minWidth: 0, width: "100%" }}>
      <style>{EDITOR_CSS}</style>
      {/* ─── Toolbar ─── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.25,
          px: 1,
          py: 0.5,
          mb: 1,
          border: `1px solid ${BORDER}`,
          borderRadius: 2,
          bgcolor: "#fff",
        }}
      >
        {showBackTab && (
          <>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={side}
              onChange={(e, v) => v && onSideChange(v)}
              sx={{ mr: 0.5, "& .MuiToggleButton-root": { py: 0.25, px: 1, fontSize: "0.7rem", textTransform: "none" } }}
            >
              <ToggleButton value="front">Front side · {sideCount.front}</ToggleButton>
              <ToggleButton value="back">Back side · {sideCount.back}</ToggleButton>
            </ToggleButtonGroup>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          </>
        )}
        {toolBtn("Undo (Ctrl+Z)", <UndoRounded fontSize="small" />, undo, !canUndo)}
        {toolBtn("Redo (Ctrl+Y)", <RedoRounded fontSize="small" />, redo, !canRedo)}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        {toolBtn("Zoom out", <ZoomOutRounded fontSize="small" />, () => setZoom(Math.max(0.25, scale / 1.2)))}
        <Typography
          onClick={() => setZoom(1)}
          title="Actual size"
          sx={{ fontSize: "0.7rem", minWidth: 38, textAlign: "center", cursor: "pointer", color: INK_MUTED, userSelect: "none" }}
        >
          {Math.round(scale * 100)}%
        </Typography>
        {toolBtn("Zoom in", <ZoomInRounded fontSize="small" />, () => setZoom(Math.min(3, scale * 1.2)))}
        {toolBtn("Fit to width", <FitScreenRounded fontSize="small" />, () => setZoom(null))}
        {toolBtn(
          showGrid ? "Hide grid" : "Show grid",
          showGrid ? <GridOnRounded fontSize="small" /> : <GridOffRounded fontSize="small" />,
          () => setShowGrid((v) => !v),
        )}
        <Tooltip title={`Snap to ${GRID_MM}mm grid`} arrow>
          <Typography
            onClick={() => setSnapGrid((v) => !v)}
            sx={{
              fontSize: "0.65rem",
              fontWeight: 700,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              cursor: "pointer",
              userSelect: "none",
              color: snapGrid ? "#fff" : INK_MUTED,
              bgcolor: snapGrid ? BLUE : "transparent",
              border: `1px solid ${snapGrid ? BLUE : BORDER}`,
            }}
          >
            SNAP
          </Typography>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        {toolBtn(selected.length > 1 ? "Align left edges" : "Align to page left", <AlignHorizontalLeftRounded fontSize="small" />, () => align("left"), !selected.length)}
        {toolBtn("Centre horizontally", <AlignHorizontalCenterRounded fontSize="small" />, () => align("hcenter"), !selected.length)}
        {toolBtn("Align right", <AlignHorizontalRightRounded fontSize="small" />, () => align("right"), !selected.length)}
        {toolBtn("Align top", <AlignVerticalTopRounded fontSize="small" />, () => align("top"), !selected.length)}
        {toolBtn("Centre vertically", <AlignVerticalCenterRounded fontSize="small" />, () => align("vcenter"), !selected.length)}
        {toolBtn("Align bottom", <AlignVerticalBottomRounded fontSize="small" />, () => align("bottom"), !selected.length)}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        {toolBtn("Bring to front (Ctrl+])", <FlipToFrontRounded fontSize="small" />, () => layer(1), !selected.length)}
        {toolBtn("Send to back (Ctrl+[)", <FlipToBackRounded fontSize="small" />, () => layer(-1), !selected.length)}
        {toolBtn("Duplicate (Ctrl+D)", <ContentCopyRounded fontSize="small" />, duplicate, !selected.length)}
        {toolBtn("Delete (Del)", <DeleteRounded fontSize="small" />, removeSelected, !selected.length)}
        <Typography noWrap sx={{ ml: "auto", fontSize: "0.68rem", color: INK_MUTED, pl: 1, maxWidth: { xs: "100%", sm: 320 } }}>
          {ov}
        </Typography>
      </Box>

      {/* ─── Stage ─── */}
      <Box
        ref={stageRef}
        className="tc-editor-stage"
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes("application/x-ticket-element")) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
          }
        }}
        onDrop={onDrop}
        sx={{
          position: "relative",
          flex: 1,
          minHeight: { xs: 420, md: 0 },
          overflow: "auto",
          bgcolor: "#EEF1F6",
          border: `1px solid ${BORDER}`,
          borderRadius: 3,
          p: 3,
          display: "flex",
          justifyContent:
            scale * paperW * (companion ? 2 : 1) * PX_PER_MM + 48 > (stageRef.current?.clientWidth || 0)
              ? "flex-start"
              : "center",
          alignItems: "flex-start",
        }}
      >
        {companion?.position === "left" && renderCompanion()}
        <Box sx={{ position: "relative", order: 2 }} className="tc-editing">
          {companion && copyCaption(companion.activeLabel, true)}
          <CanvasSheet
            ref={sheetRef}
            {...sheetProps}
            elements={sideEls}
            paperW={paperW}
            paperH={paperH}
            scale={scale}
            showGuides
            gridMm={showGrid ? GRID_MM : 0}
            // No drawn safe-area box (clients read the red dashes as part of the
            // ticket); the margin still exists as a snap guide below.
            safeMarginMm={0}
            renderEditorProps={(el) => ({
              selected: selectedIds.includes(el.id),
              onDoubleClick: () => onRequestEdit && onRequestEdit(el.id),
            })}
          />
        </Box>
        {companion?.position === "right" && renderCompanion()}
        {/* An empty reverse looks like a broken page; say what it is for. */}
        {side === "back" && sideEls.length === 0 && (
          <Typography
            sx={{
              position: "absolute",
              top: "38%",
              left: "50%",
              transform: "translateX(-50%)",
              width: 300,
              textAlign: "center",
              fontSize: "0.8rem",
              color: INK_MUTED,
              bgcolor: "rgba(255,255,255,0.92)",
              border: `1px dashed ${BORDER}`,
              borderRadius: 2,
              p: 1.5,
              pointerEvents: "none",
              zIndex: 5,
            }}
          >
            This is the reverse of the sheet. Add elements here from the
            palette, or select one on the front and set <b>Prints on → Back side</b>.
          </Typography>
        )}
        <Moveable
          ref={moveableRef}
          target={targets}
          draggable
          resizable
          rotatable={targets.length === 1}
          minWidth={minPx[0]}
          minHeight={minPx[1]}
          keepRatio={!!keepRatio}
          throttleDrag={0}
          throttleResize={0}
          throttleRotate={1}
          snappable
          snapThreshold={6}
          isDisplaySnapDigit
          snapGap
          snapDirections={{ top: true, left: true, bottom: true, right: true, center: true, middle: true }}
          elementSnapDirections={{ top: true, left: true, bottom: true, right: true, center: true, middle: true }}
          snapContainer={sheetRef.current}
          verticalGuidelines={vGuides}
          horizontalGuidelines={hGuides}
          elementGuidelines={otherEls}
          snapGridWidth={snapGrid ? GRID_MM * PX_PER_MM : 0}
          snapGridHeight={snapGrid ? GRID_MM * PX_PER_MM : 0}
          origin={false}
          padding={{ left: 0, top: 0, right: 0, bottom: 0 }}
          // ── single ──
          onDragStart={(e) => pinPx(e.target)}
          onDrag={(e) => {
            e.target.style.left = `${e.left}px`;
            e.target.style.top = `${e.top}px`;
          }}
          onDragEnd={(e) => e.isDrag && patch(commitFrame(e.target))}
          onResizeStart={(e) => pinPx(e.target)}
          onResize={(e) => {
            e.target.style.width = `${e.width}px`;
            e.target.style.height = `${e.height}px`;
            e.target.style.left = `${e.drag.left}px`;
            e.target.style.top = `${e.drag.top}px`;
          }}
          onResizeEnd={(e) => e.isDrag && patch(commitFrame(e.target))}
          onRotateStart={(e) => pinPx(e.target)}
          onRotate={(e) => {
            e.target.style.transform = e.drag.transform;
          }}
          onRotateEnd={(e) => {
            if (!e.isDrag || !e.lastEvent) return;
            const r = Math.round(((e.lastEvent.rotation % 360) + 360) % 360);
            patch(commitFrame(e.target, { rotation: r > 180 ? r - 360 : r }));
          }}
          // ── group ──
          onDragGroupStart={(e) => e.events.forEach((ev) => pinPx(ev.target))}
          onDragGroup={(e) =>
            e.events.forEach((ev) => {
              ev.target.style.left = `${ev.left}px`;
              ev.target.style.top = `${ev.top}px`;
            })
          }
          onDragGroupEnd={(e) => e.isDrag && patch(Object.assign({}, ...e.targets.map((t) => commitFrame(t))))}
          onResizeGroupStart={(e) => e.events.forEach((ev) => pinPx(ev.target))}
          onResizeGroup={(e) =>
            e.events.forEach((ev) => {
              ev.target.style.width = `${ev.width}px`;
              ev.target.style.height = `${ev.height}px`;
              ev.target.style.left = `${ev.drag.left}px`;
              ev.target.style.top = `${ev.drag.top}px`;
            })
          }
          onResizeGroupEnd={(e) => e.isDrag && patch(Object.assign({}, ...e.targets.map((t) => commitFrame(t))))}
          onClickGroup={(e) => selectoRef.current?.clickTarget(e.inputEvent, e.inputTarget)}
        />
        <Selecto
          ref={selectoRef}
          dragContainer={stageRef.current}
          selectableTargets={[".tc-editing .tc-el"]}
          hitRate={0}
          selectByClick
          selectFromInside={false}
          toggleContinueSelect={["shift"]}
          ratio={0}
          onDragStart={(e) => {
            const t = e.inputEvent.target;
            if (
              moveableRef.current?.isMoveableElement(t) ||
              targets.some((tg) => tg === t || tg.contains(t))
            ) {
              e.stop();
            }
          }}
          onSelectEnd={(e) => {
            const ids = e.selected.map((n) => n.dataset.elId).filter(Boolean);
            onSelect(ids);
            if (e.isDragStart && ids.length) {
              e.inputEvent.preventDefault();
              const first = byId[ids[0]];
              if (first && !first.locked) {
                moveableRef.current?.waitToChangeTarget().then(() => {
                  moveableRef.current?.dragStart(e.inputEvent);
                });
              }
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default TicketCanvasEditor;
